import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import map from 'lodash/map';
import filter from 'lodash/filter';
import React from 'react';
import { getKPIListForAsset, isKpiSelected, isKpiSelectedTrend, isKpiSelectedV2 } from './helpers';
import { useDispatch, useSelector } from 'react-redux';
import { removeSelectedKPI } from '../../ChartView/actions';
import { LoadingStatusWrapper } from 'sharedComponents/WithLoadingStatus';
import newCapabilityHelper from 'helpers/newCapabilityHelper';
import { contractInfoMessages, apiCallStatus } from 'helpers/constants';
import { Checkbox } from 'sharedComponents/Checkbox';
import { opButtons, chartSignalTypes } from '../../ChartView/constants';

const KPIListComponent = (props) => {
    const {
        selectedAsset,
        selectedAssetCapability,
        capabilityLoadingStatus,
        chartData,
        kpiList,
        setKpi,
        onSelectSignal,
        onDeselectSignal,
        isSignalSelected,
        selectedKPIType,
        crossAssetAnalysisActiveTab,
        searchText,
        lockedSignalKey,
        lockedSignalName
    } = props;
    const { t: translate } = useTranslation();
    const dispatch = useDispatch();
    const crossAssetSidepanel = useSelector(({ crossAssetAnalysisSidepanel }) => crossAssetAnalysisSidepanel);
    const storeSelectedKPIType = useSelector(({ chartView }) => chartView.selectedKPIType);
    const effectiveSelectedKPIType = selectedKPIType || storeSelectedKPIType;
    const resolvedKpiList = kpiList || crossAssetSidepanel?.kpiList;
    const { kpis, loadingStatus } = getKPIListForAsset(resolvedKpiList, translate, selectedAsset?.id, crossAssetAnalysisActiveTab, effectiveSelectedKPIType);
    const newcapabilityHelper = new newCapabilityHelper(selectedAssetCapability, selectedAsset);
    const hasActiveSubscription = newcapabilityHelper.subscriptionStatus() === contractInfoMessages?.activeSubscription;
    const normalizedSearchText = searchText?.trim()?.toLowerCase() || '';
    const filterSignals = (signalList) => {
        if (!normalizedSearchText) {
            return signalList;
        }

        return filter(signalList, (kpi) => {
            const label = (kpi?.displayName || kpi?.name || '').toLowerCase();
            return label.includes(normalizedSearchText);
        });
    };
    const filteredStandardKPIList = filterSignals(kpis?.standardKPIList);
    const filteredParameterKPIList = filterSignals(kpis?.parameterKPIList);

    const toggleMeasurement = (kpi, isSelected) => {
        if (isSelected) {
            console.log('removeSelectedKPI', kpi, selectedAsset, selectedKPIType);
            dispatch(removeSelectedKPI(kpi, selectedAsset, selectedKPIType, crossAssetAnalysisActiveTab));
        }
        else {
            console.log('SelectedKPI', kpi, selectedAsset, selectedKPIType);
            dispatch(setKpi({ ...kpi, isChecked: true }, selectedAsset));
        }
    };
    const selectKPICheckbox = (kpi, isSelected) => {
        if ([opButtons.histogramV2, opButtons.scatterPlotV2].includes(crossAssetAnalysisActiveTab) || kpi?.isPlottableToTrendChart) {
            toggleMeasurement(kpi, isSelected);
        }
    };

    const renderKpiRow = (kpi, index, sectionKey) => {
        let isSelected;
        if (effectiveSelectedKPIType === 1) {
            isSelected = isKpiSelectedTrend(chartData, kpi);
        } else if ([opButtons.histogramV2, opButtons.scatterPlotV2].includes(crossAssetAnalysisActiveTab)) {
            isSelected = isKpiSelectedV2(chartData, kpi, crossAssetAnalysisActiveTab === opButtons.scatterPlotV2);
        } else {
            isSelected = isKpiSelected(chartData, kpi);
        }
        const resolvedIsSelected = typeof isSignalSelected === 'function'
            ? isSignalSelected(kpi, isSelected)
            : isSelected;

        // Tag the locked signal row so CSS can enable it without touching the checkbox state
        const isLockedRow = lockedSignalKey && (
            kpi?.scatterKey === lockedSignalKey ||
            kpi?.histogramKey === lockedSignalKey ||
            lockedSignalName && (kpi?.displayName || kpi?.name) === lockedSignalName
        );
        const isDisabledByLock = !!(lockedSignalKey && !isLockedRow);
        const isDisabledInTrend = ![opButtons.histogramV2, opButtons.scatterPlotV2].includes(crossAssetAnalysisActiveTab) && !kpi?.isPlottableToTrendChart;
        // Must match the Checkbox's disabled state so clicking the row text can't bypass it
        const isRowDisabled = isDisabledByLock || isDisabledInTrend;

        const handleKpiRowClick = () => {
            if (isRowDisabled) { return; }
            if (resolvedIsSelected) {
                if (typeof onDeselectSignal === 'function') {
                    onDeselectSignal(kpi, { isVisibleSelected: isSelected });
                } else {
                    selectKPICheckbox(kpi, isSelected);
                }
            } else if (typeof onSelectSignal === 'function') {
                onSelectSignal(kpi, selectedAsset);
            } else {
                selectKPICheckbox(kpi, isSelected);
            }
        };

        return <button
            type='button'
            className={`kpi-list-item${resolvedIsSelected ? ' selected' : ''}${isLockedRow ? ' kpi-list-item--locked' : ''}`}
            onClick={handleKpiRowClick}
            disabled={isRowDisabled}
            key={`${sectionKey}-${kpi?.timeseriesKey || kpi?.measurementTypeKey || kpi?.histogramKey || kpi?.scatterKey || index}`}>
            <div aria-hidden='true'>
                <Checkbox
                    checked={resolvedIsSelected}
                    disabled={isRowDisabled || ![opButtons.histogramV2, opButtons.scatterPlotV2].includes(crossAssetAnalysisActiveTab) && !kpi?.isPlottableToTrendChart}
                />
            </div>
            <div className={`kpi-name ${isRowDisabled ? ' kpi-name--disabled' : ''}`}>
                {kpi?.displayName || kpi?.name}
            </div>
        </button>;
    };

    return (
        <LoadingStatusWrapper
            className='loading-indicator-content'
            loadingStatus={[loadingStatus, capabilityLoadingStatus, selectedAssetCapability.loadingStatus]}
            indicatorConfig={{ size: 'small', style: { 'padding': '8px 0px' } }}>
            <div className='kpi-list'>
                {
                    //need to check alternative for isUserAccessible not available in microservices.
                    <>
                        {
                            loadingStatus === apiCallStatus.SUCCESS && !kpis?.standardKPIList?.length &&
                            !kpis?.parameterKPIList?.length &&
                            hasActiveSubscription &&
                            <div className='kpi-list-empty'>{translate('ABB.Powertrain.Frontend.noSupportedMeasurementTypes')}</div>
                        }
                        {
                            loadingStatus === apiCallStatus.SUCCESS &&
                            (kpis?.standardKPIList?.length || kpis?.parameterKPIList?.length) > 0 &&
                            !filteredStandardKPIList?.length &&
                            !filteredParameterKPIList?.length &&
                            hasActiveSubscription &&
                            <div className='kpi-list-empty'>{translate('ABB.Powertrain.Frontend.noSupportedMeasurementTypes')}</div>
                        }
                        {
                            hasActiveSubscription ?
                                <>
                                    {map(filteredStandardKPIList, (kpi, index) => renderKpiRow(kpi, index, chartSignalTypes.standard))}
                                    {
                                        filteredParameterKPIList?.length > 0 &&
                                        <div className='kpi-list-divider'>
                                            <span className='kpi-list-divider-label'>
                                                {translate('ABB.Powertrain.Frontend.additionaltrends')}
                                            </span>
                                        </div>
                                    }
                                    {map(filteredParameterKPIList, (kpi, index) => renderKpiRow(kpi, index, chartSignalTypes.additional))}
                                </>
                                :
                                <div className='kpi-list-empty'>{translate('ABB.Powertrain.Frontend.noSubscriptionOrContract')}
                                </div>
                        }
                    </>
                }
            </div>
        </LoadingStatusWrapper >
    );
};

KPIListComponent.propTypes = {
    selectedAsset: PropTypes.any,
    kpiList: PropTypes.object,
    kpis: PropTypes.any,
    chartData: PropTypes.array,
    selectedAssetCapability: PropTypes.object,
    setKpi: PropTypes.func,
    onSelectSignal: PropTypes.func,
    onDeselectSignal: PropTypes.func,
    isSignalSelected: PropTypes.func,
    selectedKPIType: PropTypes.number,
    capabilityLoadingStatus: PropTypes.string,
    crossAssetAnalysisActiveTab: PropTypes.string,
    searchText: PropTypes.string,
    lockedSignalKey: PropTypes.string,
    lockedSignalName: PropTypes.string,
};

export default KPIListComponent;
